// Utils & misc
import cn from '../../../utils/cn';

// Components
import { Skeleton } from '../Skeleton/Skeleton';

// Style
import style from './Avatar.module.css';

/**
 * Avatar component - Displays user avatar with optional notification badge
 * @param {object} params
 * @param {string} params.src - Avatar image source URL
 * @param {string} params.alt - Alt text for the avatar image
 * @param {boolean} [params.isMenuOpen=false] - Whether the navigation menu is open
 * @param {number} [params.notificationCount] - Number of notifications (optional, shows dot if not provided)
 * @param {(event: React.MouseEvent | React.KeyboardEvent) => void} [params.onClick] - Click handler for the avatar
 * @param {string} [params.className] - Additional CSS classes
 * @param {boolean} [params.isLoading=false] - Whether the avatar is loading
 * @returns {JSX.Element} Avatar component
 */
function Avatar({
  src,
  alt,
  isMenuOpen = false,
  notificationCount,
  onClick,
  className,
  isLoading = false,
}) {
  /**
   * Handle click event
   * @param {React.MouseEvent | React.KeyboardEvent} event - Click event
   */
  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }
  };

  /**
   * Avatar content
   * @returns {JSX.Element} Avatar content
   */
  const avatarContent = (
    <div className={cn([style['avatar-container']])}>
      <img
        src={src}
        alt={alt}
        className={cn([style.avatar])}
      />

      {notificationCount && notificationCount > 0 ? (
        <div className={cn([style['notification-badge']])}>
          <span className={cn([style['notification-count']])}>
            {notificationCount > 99 ? '99+' : notificationCount}
          </span>
        </div>
      ) : null}
    </div>
  );

  if (isLoading) {
    return (
      <Skeleton shape="round" width="32px" height="32px" noAnimation />
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={cn([
          style['navigation-avatar'],
          ...(isMenuOpen ? [style['menu-open']] : []),
          style.clickable,
          ...(className ? [className] : []),
        ])}
        onClick={handleClick}
      >
        {avatarContent}
      </button>
    );
  }

  return (
    <div
      className={cn([
        style['navigation-avatar'],
        ...(isMenuOpen ? [style['menu-open']] : []),
        ...(className ? [className] : []),
      ])}
    >
      {avatarContent}
    </div>
  );
}

export default Avatar;
